const TOKEN_KEY = 'mbpj_cctv_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

const request = async (path, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(path, { ...options, headers });
  const body = await response.json().catch(() => ({}));

  if (response.status === 401 && !path.includes('/auth/login')) {
    setToken(null);
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  }

  if (!response.ok || body.success === false) {
    const error = new Error(body.message || 'Request failed.');
    error.status = response.status;
    throw error;
  }

  return body.data;
};

export const api = {
  login: (username, password) =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  me: () => request('/api/auth/me'),
  dssStatus: () => request('/api/dss/status'),
  summary: (mode, date, start, end) => {
    const params = new URLSearchParams({ mode, date });
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request(`/api/uptime/summary?${params}`);
  },
  report: (mode, date, start, end) => {
    const params = new URLSearchParams({ mode, date });
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return request(`/api/uptime/daily?${params}`);
  },
  history: (mode) =>
    request(`/api/uptime/history${mode ? `?mode=${encodeURIComponent(mode)}` : ''}`),
  statusPage: (days = 60) => request(`/api/uptime/status?days=${encodeURIComponent(days)}`),
  poll: () => request('/api/uptime/poll', { method: 'POST' }),
  exportUptime: async (mode, date, start, end) => {
    const token = getToken();
    const params = new URLSearchParams({ mode, date });
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const response = await fetch(`/api/uptime/export?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.status === 401) {
      setToken(null);
      window.location.assign('/login');
      throw new Error('Session expired.');
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Failed to export Excel.');
    }
    const blob = await response.blob();
    const match = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') || '');
    return { blob, filename: match ? match[1] : `AeroNMS-CCTV-Uptime-${mode}-${date}.xlsx` };
  },
  health: (date, refresh = false) => {
    const params = new URLSearchParams({ date });
    if (refresh) params.set('refresh', '1');
    return request(`/api/health?${params}`);
  },
  healthRefresh: (date) =>
    request('/api/health/refresh', {
      method: 'POST',
      body: JSON.stringify({ date }),
    }),
  exportHealth: async (date) => {
    const token = getToken();
    const params = new URLSearchParams({ date });
    const response = await fetch(`/api/health/export?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.status === 401) {
      setToken(null);
      window.location.assign('/login');
      throw new Error('Session expired.');
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Failed to export Excel.');
    }
    const blob = await response.blob();
    const match = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') || '');
    return { blob, filename: match ? match[1] : `AeroNMS-CCTV-Health-${date}.xlsx` };
  },
  devices: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.uptime !== undefined) params.set('uptime', filters.uptime ? 'true' : 'false');
    const query = params.toString();
    return request(`/api/devices${query ? `?${query}` : ''}`);
  },
  devicePreview: async (id, { refresh = false } = {}) => {
    const token = getToken();
    const params = refresh ? '?refresh=1' : '';
    const response = await fetch(`/api/devices/${id}/preview${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.status === 401) {
      setToken(null);
      window.location.assign('/login');
      throw new Error('Session expired.');
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Preview is not available.');
    }
    return response.blob();
  },
  deviceLive: (id, streamType = '2') =>
    request(`/api/devices/${id}/live?streamType=${encodeURIComponent(streamType)}`),
  livePlaylistUrl: (id, streamType = '2') => {
    const params = new URLSearchParams({ streamType });
    const token = getToken();
    if (token) params.set('access_token', token);
    return `/api/devices/${id}/live.m3u8?${params}`;
  },
  syncDevices: () => request('/api/devices/sync', { method: 'POST' }),
  updateDevice: (id, payload) =>
    request(`/api/devices/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  users: () => request('/api/users'),
  createUser: (payload) =>
    request('/api/users', { method: 'POST', body: JSON.stringify(payload) }),
  updateUser: (id, payload) =>
    request(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),
};
